<template>
  <div
    class="view"
    style="height: fit-content; background-position: top center; background-repeat: no-repeat; background-size: cover; background-image: linear-gradient(to bottom, #FEFFFF 0%, 32.6605498790741%, #B3CEE2 65.3210997581482%, 82.6605498790741%, #8DE3DA 100%);"
  >
    <div v-if="loadingConsentForm">
      <ProgressSpinner
        class="medium-icon"
        stroke-width="8"
        animation-duration=".75"
        aria-label="Loading consent form..."
      />
      <p>Loading consent form...</p>
    </div>
    <div v-else-if="consentValidationError!==''">
      <p>Consent form is invalid / already submitted</p>
    </div>
    <div
      v-else 
      class="container main"
    >
      <h1
        class="form-header consent-header"
      >
        Patient Consent Form
      </h1>
      <hr style="width:100%;text-align:left;margin-left:0; height: 5px; background-color: #008491; border: none; margin-top: 20px;">
      <div style="display: flex; align-items: center; margin-top: 20px; gap: 20px; flex-wrap: wrap">
        <p>Powered By </p>
        <a
          href="https://www.strongroom.ai/"
          target="_blank"
        >
          <img
            style="width: 220px; height: auto;"
            :src="strongProLogoSrc"
            alt="Strong Pro Logo"
          >
        </a>
      </div>
      <Transition name="fade">
        <div v-if="!registered">
          <!-- INTRO SECTION -->
          <section
            class="section"
            style="padding-top: 0;"
          >
            <div

              class="main-para"
            >
              <h3>Introduction</h3>
              <p
                v-if="marketingSequence && healthOutreachSequence"
              >
                At <strong>{{ location.name }}</strong>, we value your privacy and want to ensure you receive only the communications that are relevant to you. Please use the options below to set your consent preferences for receiving Health Outreach and Marketing communications from our pharmacy.
              </p>
              <p
                v-else-if="marketingSequence"
              >
                At <strong>{{ location.name }}</strong>, we value your privacy and want to ensure you receive only the communications that are relevant to you. Please use the options below to set your consent preferences for receiving Marketing communications from our pharmacy.
              </p>
              <p
                v-else-if="healthOutreachSequence"
              >
                At <strong>{{ location.name }}</strong>, we value your privacy and want to ensure you receive only the communications that are relevant to you. Please use the options below to set your consent preferences for receiving Health Outreach communications from our pharmacy.
              </p>
            </div>
          </section>
          <!-- REASON SECTION -->
          <section>
            <Accordion
              :expand-icon="'pi pi-chevron-down'"
              :collapse-icon="'pi pi-chevron-up'"
            >
              <AccordionTab 
                v-if="healthOutreachSequence"
                header="What is Health Outreach?"
                class="main-para"
              >
                <p>
                  Health Outreach includes important healthcare-related communications designed to support your well-being. They aim to help you manage your health effectively and stay informed about available healthcare support. These may include:
                </p>
                <ul>
                  <li>Information about healthcare services, consultations, and programs.</li>
                  <li>Educational resources on health conditions, treatments, and preventive care.</li>
                  <li>Patient safety and compliance messages.</li>
                  <li>Appointment and health check-up reminders.</li>
                  <li>Prescription refill notifications and medication adherence reminders.</li>
                </ul>
              </AccordionTab>
              <AccordionTab 
                v-if="marketingSequence"
                header="What is Marketing?"
                class="main-para"
              >
                <p>
                  Marketing communications from {{ location.name }} provide updates on our latest offers and services. By opting in, you can stay informed about savings and opportunities that may be beneficial to you. These may include:
                </p>
                <ul>
                  <li>Information on pharmacy promotions, discounts, and sales.</li>
                  <li>Exclusive special offers for loyal customers.</li>
                  <li>Newsletters with updates on new products and services.</li>
                  <li>Invitations to community health events and wellness programs.</li>
                </ul>
              </AccordionTab>
            </Accordion>
            <div
              class="main-para"
            >
              <p>
                <strong>Your preferences matter.</strong> You have full control over the communications you receive. You can opt-out of these communications anytime by replying STOP to the SMS, or using unsubscribe link in the emails.
              </p>
              <p>
                If you have any questions or need further assistance, please speak to one of our pharmacy staff members.
              </p>
            </div>
          </section>
          <!-- FORM SECTION -->
          <section 
            class="section grey-section" 
            style="margin: 0; border-top-left-radius: 0px; border-top-right-radius: 0px;"
          >
            <div class="signup-container">
              <div class="form-container signup-container__form">
                <div
                  class="flex-input"
                >
                  <label
                    for="phone_number"
                    style="font-size: 15px;"
                  >
                    Phone Number
                  </label>
                  <InputText
                    v-model="registrationForm.phone_number"
                    placeholder="Phone Number"
                    :disabled="patientDetailStatus.phone_number"
                    class="grey-input flex-expand"
                  />
                </div>
                <div
                  class="flex-input"
                >
                  <label
                    for="email"
                    style="font-size: 15px;"
                  >
                    Email
                  </label>
                  <InputText
                    v-model="registrationForm.email"
                    placeholder="Email"
                    :disabled="patientDetailStatus.email"
                    class="grey-input flex-expand"
                  />
                </div>
                <div  
                  class="two-col"
                >
                  <div
                    v-if="healthOutreachSequence"
                    class="flex-input"
                  >
                    <label
                      style="font-size: 15px;"
                      for="healthOutreachSmsConsent"
                    >
                      SMS Health Outreach
                    </label>
                    <InputSwitch
                      v-model="healthOutreachSmsConsent"
                    />
                  </div>
                  <div  
                    v-if="healthOutreachSequence"
                    class="flex-input"
                  >
                    <label
                      style="font-size: 15px;"
                      for="healthOutreachEmailConsent"
                    >
                      Email Health Outreach
                    </label>
                    <InputSwitch
                      v-model="healthOutreachEmailConsent"
                    />
                  </div>
                  <div
                    v-if="marketingSequence"
                    class="flex-input"
                  >
                    <label
                      style="font-size: 15px;"
                      for="marketingSmsConsent"
                    >
                      SMS Marketing
                    </label>
                    <InputSwitch
                      v-model="marketingSmsConsent"
                    />
                  </div>
                  <div 
                    v-if="marketingSequence" 
                    class="flex-input"
                  >
                    <label
                      style="font-size: 15px;"
                      for="marketingSmsConsent"
                    >
                      Email Marketing
                    </label>
                    <InputSwitch
                      v-model="marketingEmailConsent"
                    />
                  </div>
                </div>
                <div class="form-row two-col">
                  <p 
                    style="font-size: 15px;"
                  >
                    *By submitting this form, you agree to the terms and conditions of that pharmacy can send SMS and email notifications to you. You can opt-out any time.
                  </p>
                </div>
                <VueRecaptcha
                  class="two-col captcha"
                  :sitekey="siteKey"
                  @verify="verifyCaptcha"
                  @expired="expiredCaptcha"
                  @render="renderCaptcha"
                  @error="captchaError"
                />
                <Button
                  v-if="!loading"
                  class="purple-button two-col"
                  label="Submit"
                  :disabled="!captcha_verified"
                  @click="signup"
                />
                <div
                  v-else
                  class="flex"
                >
                  <ProgressSpinner
                    class="medium-icon"
                    stroke-width="8"
                    animation-duration=".75"
                    fill="white"
                    aria-label="Signing you up..."
                  />
                  Signing you up...
                </div>
              </div>
            </div>
            <p
              v-if="registrationError"
              class="error-message"
            >
              {{ registrationError }}
            </p>
            <p
              v-if="registrationSuccess"
              class="success-message"
            >
              {{ registrationSuccess }}
            </p>
          </section>
        </div>
        <section
          v-else
          class="section"
        >
          <div class="main-para">
            Your preferences have been successfully submitted. Thank you for your time.
          </div>
        </section>
      </Transition>
    </div>
  </div>
  <footer>
    <div class="footer-content grey-section">
      <div class="footer-logos">
        <div class="footer-logo">
          <img
            :src="lockupLogoSrc"
            alt="StrongRoom AI Logo"
          >
          <div class="footer-tnc">
            <p>
              <a
                href="https://strongroom.ai/privacy/"
                target="_blank"
                style="color: black; text-decoration: none;"
              >Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
    <!-- <hr
        style="margin-right: -10px; width: 2px; background-color: grey; border: none; height: 50px; margin-top: 3px;"
        class="signup-container__vertline"
      > -->
  </footer>
</template>

<script>
import { VueRecaptcha } from 'vue-recaptcha';
import axios from 'axios';

export default {
  name: 'CommunicationConsentFormPage',
  components: {
    VueRecaptcha,
  },
  data() {
    return {
      lockupLogoSrc: require('@/assets/logos/Lockup_Colour_Black.svg'),
      lockupLogoWhiteSrc: require('@/assets/logos/Lockup_Colour_White.svg'),
      isoLogoSrc: require('@/assets/logos/iso_27001.png'),
      hipaaLogoSrc: require('@/assets/logos/hipaa.png'),
      aesLogoSrc: require('@/assets/logos/aes_256.png'),
      strongProLogoSrc: require('@/assets/logos/StrongPro_logo.svg'),
      siteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
      timerCounter: 0,
      loadingConsentForm: true,
      consentValidationError: '',
      registrationForm: {
        first_name: null,
        last_name: null,
        email: null,
        phone_number: null,
        postcode: null,
      },
      marketingSequence: false,
      healthOutreachSequence: false,
      marketingSmsConsent: null,
      marketingEmailConsent: null,
      healthOutreachSmsConsent: null,
      healthOutreachEmailConsent: null,
      patientDetailStatus: {
        first_name: false,
        last_name: false,
        email: false,
        phone_number: false,
        postcode: false,
      },
      captcha_verified: '',
      registered: false,
      registrationError: '',
      registrationSuccess: '',
      loading: false,
      consentId: null,
      location: {
        name: '',
        phone_number: '',
        street_address: '',
        town: '',
        region: '',
        postcode: ''
      },
    }
  },
  computed: {
    registrationPayload() {

      let payload = {
        health_outreach_sms_consent: null,
        health_outreach_email_consent: null,
        marketing_sms_consent: null,
        marketing_email_consent: null,
      }
      if (this.healthOutreachSequence) {
        payload.health_outreach_sms_consent = this.healthOutreachSmsConsent;
        payload.health_outreach_email_consent = this.healthOutreachEmailConsent;
      }
      if (this.marketingSequence) {
        payload.marketing_sms_consent = this.marketingSmsConsent;
        payload.marketing_email_consent = this.marketingEmailConsent;
      }
      
      return payload;
    }
  },
  async mounted() {
    // if (this.$userback) {
    //   this.$userback.hide();
    // }
    this.runTimer();
    await this.getConsentForm();
  },
  methods: {
    
    async getConsentForm() {
      this.loadingConsentForm = true;

      await axios.post('/api/communication-consent/'+this.$route.params.id, {})
      .then((res) => {
        this.handleConsentFormSuccess(res);
      })
      .catch((error) => {
        this.handleConsentFormError(this.handleAxiosError(error));
      });
    },
    /**
     * Handle the success response for consent form
     */
     handleConsentFormSuccess(res) {
      console.log(res.data.data);
      this.loadingConsentForm = false;
      this.registrationForm.first_name= res.data.data.first_name;
      this.registrationForm.last_name= res.data.data.last_name;
      this.registrationForm.email= res.data.data.email;
      this.registrationForm.phone_number= res.data.data.phone_number;
      this.registrationForm.postcode= res.data.data.postcode;

      this.consentId = res.data.data.consent_id;
      this.location = res.data.data.location;

      this.marketingSequence = res.data.data.marketing_consent;
      this.healthOutreachSequence = res.data.data.health_outreach_consent;

      this.marketingSmsConsent = null;
      this.marketingEmailConsent = null;
      this.healthOutreachSmsConsent = null;
      this.healthOutreachEmailConsent = null;

      if (this.marketingSequence) {
        this.marketingSmsConsent = false;
        this.marketingEmailConsent = false;
      }
      if (this.healthOutreachSequence) {
        this.healthOutreachSmsConsent = true;
        this.healthOutreachEmailConsent = true;
      }

      for (const key in this.patientDetailStatus) {
        if (this.registrationForm[key]) {
          this.patientDetailStatus[key] = true;
        }
      }
    },
    /**
     * Handle the error response for consent form
     * @param {Object} err
     */
    handleConsentFormError(err) {
      this.consentValidationError = err;
      this.loadingConsentForm = false;
      console.log('The consent form error is: ', err);
    },
    /**
     * Run the timer to hide the feedback button
     */
    async runTimer() {
      setTimeout(() => {
        let elements = document.getElementsByClassName('bb-feedback-button-classic');
        for (let i = 0; i < elements.length; i++) {
          elements[i].style.display = 'none';
        }
        this.timerCounter++;
        if (this.timerCounter < 10) {
          this.runTimer();
        }

      }, 500);
    },
    /**
     * Signup the pharmacy
     * @returns {Promise<void>}
     */
    async signup() {
      this.registrationError = '';
      this.registrationSuccess = '';
      this.loading = true;

      if (!this.registrationForm.first_name || !this.registrationForm.last_name || !this.registrationForm.email || !this.registrationForm.phone_number || !this.registrationForm.postcode) {
        this.handleRegistrationError('Please fill in all the fields.');
        return;
      }

      await axios.post(`/api/communication-consent/${this.consentId}/submit`, this.registrationPayload)
      .then((response) => {
        this.handleRegistrationSuccess(response);
      })
      .catch((error) => {
        this.handleRegistrationError(this.handleAxiosError(error));
      });
    },
    /**
     * Handle the success response for registration
     * @param {Object} res
     */
    handleRegistrationSuccess(res) {
      console.log('The registration response is: ', res);
      this.loading = false;
      this.registrationSuccess = 'Your preferences have been successfully submitted. Thank you for your time.';
      this.registered = true;
    },
    /**
     * Handle the error response for registration
     * @param {Object} err
     */
    handleRegistrationError(err) {
      console.log('The registration error is: ', err);
      this.loading = false;
      this.registrationError = err;
    },
    verifyCaptcha(response) {
      console.log("Captcha verified: ", response);
      this.captcha_verified = response;
    },
    expiredCaptcha() {
      console.log("Captcha expired");
      this.captcha_verified = '';
    },
    renderCaptcha(id) {
      console.log("Captcha rendered: ", id);
    },
    captchaError() {
      console.log("Captcha error");
      this.captcha_verified = '';
    },
    clear() {
      this.registrationForm.first_name = '';
      this.registrationForm.last_name = '';
      this.registrationForm.email = '';
      this.registrationForm.phone_number = '';
      this.registrationForm.postcode = '';
      this.captcha_verified = '';
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../assets/css/main.scss";

p {
  font-size: 1.25rem;
}

.view {
  justify-content: center;
}

.flex-row {
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
}

.flex-row > a {
  display: flex;
  align-items: center;
  justify-content: center;
}

.flex-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
  text-align: left;
}

.flex-item {
  flex: 1;
}

.header {
  border-bottom: 1px solid grey;
  position: relative;
  width: 100%;
}

.form-header {
    padding-top: 2rem;
}

.consent-header {
  color: #00205B; 
  font-size: 2em; 
  word-wrap: break-word
}

.container {
  width: 100%;
  text-align: left;
  margin-left: auto;
  margin-right: auto;
}

.header > .container {
  padding-bottom: 1.25rem;
  padding-top: 1.25rem;
  height: 5rem;
}

.header-logo {
  width: 12rem;
  height: 100%;
}

.intro-location-name {
  font-weight: 700;
}

.black-button a {
  text-decoration: none;
  color: inherit;
}

.main {
  padding-bottom: 2rem;
}

.section {
  padding-top: 2rem;
}
.grey-section{
  background-color: #f3f4f4;
  padding: 32px;
  border-radius: 6px;
}

.main-para {
  font-size: 1.25rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;

  ol {
    margin-top: 0px;
  }
}
.recall-intro {
  font-size: 1.25rem;
  font-weight: 600;
}

.important-text{
  display: grid;
  grid-template-columns: 20px 1fr;
  margin-top: 30px;
  background-color: #f5f5f5;
  border-radius: 6px;
}

.important-text-content{
  padding: 29px;
}

.sign-up-container{
  color: white;
  background-color: #101820;
  padding: 32px;
  border-top-right-radius: 6px;
  border-top-left-radius: 6px;
}

.purple-border{
  background-color: #6930A7;
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.flex-expand {
  flex: 1;
}

.flex-icons {
  display: grid;
  grid-template-columns: [first] 150px [line2] auto;
  gap: 1rem;
  p {
    font-size: 15px;
  }
}

.app-logos {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  margin: 15px 0;
  gap: 15px;
}

.logo-item {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: left;
  justify-self: flex-start;
  flex: 1;
  max-width: 270px;
  text-align: left;
  gap: 0.5rem;

  img {
    max-height: 52px;
    min-height: 52px;
    align-self: flex-start;
    flex: 0 0 auto;
  }
}

.footer-logo-separator {
  width:2px;
  text-align:left;
  margin-left:0; 
  height: 100px; 
  background-color: grey; 
  border: none; 
  margin-top: 20px;
}

.engagement-links {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 15px;

  a {
    text-decoration: none;
    color: inherit;
  }
}

.grey-button {
  padding-left: 3rem !important;
  padding-right: 3rem !important;
}

.image-div {
  width: 150px;
  text-align: center;
}

.cert-image {
  max-height: 100px;
  max-width: 150px;
  width: auto;
  height: auto;
}

.cert-item {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;

  p {
    margin: 0;
  }
}

.signup-container {
  height: 100%;
}

.signup-container__form {
  grid-area: form;
}

.signup-container__vertline {
  grid-area: vertline;
}

.signup-container__security {
  grid-area: security;
}

.left-col {
  grid-column-start: 1;
  grid-column-end: 2;
}

.right-col {
  grid-column-start: 4;
  grid-column-end: 5;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
}

.flex {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.form-row {
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: stretch;

  .span {
    display: flex;
  }
}
.two-col {
  grid-area: span 1 / span 2;
}

.error-message {
  color: $error;
}

.success-message {
  color: $correct;
}

.p-progress-spinner {
  margin: 0 0 0 0 !important;
}

.flex-right {
  margin-left: auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.purple-button{
  background-color:#6930A7;
  color: white;
  font-weight: bold;
  border: none;
}

:deep(.p-accordion-header-link) {
  background: none;
  font-family: Montserrat, Helvetica, Arial, sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  border:none;
}

:deep(.p-accordion .p-accordion-content) {
  background: none;
  font-family: Montserrat, Helvetica, Arial, sans-serif;
  border: none;

  li {
    line-height: 1.75em;
    font-size: 1.25rem;
  }
}

footer {
  background-color: #f3f4f4;
  color: white;
  width: 100%;
  padding-left: 40px;
  padding-right: 40px;
}

.footer-content {
  max-width: 1150px;
  margin: 0 auto;
  padding: 51px 0;

  p {
    font-size: 1.25rem;
    font-weight: bold;
    text-align: left;
  }
}

.footer-misc-text {
  display: flex;
  flex-direction: column;

  b {
    font-size: 1rem;
    text-align: left;
    color: $deepgrey;
    font-weight: 600;
  }

  p {
    margin-top: 0;
    font-size: 0.75rem;
    text-align: left;
    color: $deepgrey;
    font-weight: 500;
  }
}

.footer-logos {
  display: flex;
  gap: 50px;
  max-width: 800px;
}

.footer-logo {
  flex: 1;

  img {
    display: flex;
    max-width: 300px;
    height: 80px;
  }
}

.captcha {
  margin: 0; 
  display: table;
}

.bb-feedback-button-classic {
  display: none;
}

.footer-tnc {
  display: flex;
  justify-content: flex-start;
  max-width: 400px;
  align-items: center;
}

@media (max-width: 768px) {
  .footer-logo-separator {
    width:100%;
    text-align:left;
    margin-left:0; 
    height: 2px; 
    background-color: grey; 
    border: none; margin-top: 10px;
  }

  .footer-logos {
    flex-direction: column;
    gap: 0px;
  }
}

@media (max-width: 420px) {
  .captcha {
    transform:scale(0.9);
    -webkit-transform:scale(0.9);
    transform-origin:0 0;
    -webkit-transform-origin:0 0;
  }

  .consent-header {
    font-size: 1.8em;
  }

  .form-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}

@media (max-width: 390px) {
  .captcha {
    transform:scale(0.7);
    -webkit-transform:scale(0.7);
  }

  .consent-header {
    font-size: 1.5em;
  }
}

@media (max-width: 330px) {
  .captcha {
    transform:scale(0.6);
    -webkit-transform:scale(0.6);
  }
  .footer-logos {
    gap: 20px;
  }

  .consent-header {
    font-size: 1.3em;
  }
}

@media (max-width: 300px) {
  .captcha {
    transform:scale(0.53);
    -webkit-transform:scale(0.53);
  }
  .footer-logos {
    gap: 20px;
  }
}


@media (min-width: 768px) {
  .container {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

@media (min-width: 1048px) {
  .signup-container {
    display: grid;
    row-gap: 20px;
    column-gap: 20px;
    height: 100%;
    grid-template-areas: 'form vertline security';
    grid-template-columns: minmax(500px, 1fr);
  }
}

@media (max-width: 1048px) {
  .signup-container__vertline {
    display: none;
  }
}

@media (min-width: 1320px) {
  .container {
    width: 1240px;
  }
}
</style>